import Autosuggest from 'react-autosuggest';
import React, { useState, useEffect, useContext } from 'react';
import sortBy from 'lodash.sortby';
import styles from './styles.css';
import { clean } from '../FundListFragment/util';
import { updateCusips } from '../ManualInstitution/utils';
import { CartContext } from '../../context/CartContext/CartContext';

const AUTOSUGGEST_MIN_CHARS = 3;

export const getSuggestionName = suggestion =>
  `${suggestion.name} (${suggestion.ticker})`;

export const getSuggestionValue = suggestion => {
  return typeof suggestion === 'object'
    ? getSuggestionName(suggestion)
    : 'none';
};

export const renderInputComponent = inputProps => {
  return (
    <div className="auto-suggest-input">
      <input
        className="search-input"
        id="quick-search-input-funds"
        {...inputProps}
      />
      <span className="search-icon" />
      <style jsx>{styles}</style>
    </div>
  );
};

export const getFundId = suggestion => suggestion.ticker;

export const renderSuggestion = suggestion => {
  const contents =
    suggestion && suggestion.name ? (
      <div
        id={`fund${getFundId(suggestion)}`}
        className={`fund-suggestion${
          suggestion.openToNewInvestors === false ? ' fund-disabled' : ''
        }`}
        data-value={getFundId(suggestion)}>
        {getSuggestionName(suggestion)}
        <style jsx>{styles}</style>
      </div>
    ) : (
      <div id="no-result" className="fund-suggestion" data-value="no results">
        Could Not Find Any Funds Available
        <style jsx>{styles}</style>
      </div>
    );
  return (
    <div className="returned-suggestions">
      {contents}
      <style jsx>{styles}</style>
    </div>
  );
};

const isCustodianFundValNone = (mode, suggestedVal) => {
  return (
    (mode === 'FONASU-transfer' || mode === 'MI') && suggestedVal === 'none'
  );
};

const FundQuickSearch = ({
  updateFilterText,
  allMutualFunds,
  placeholder,
  ...rest
}) => {
  const cleanedFunds = allMutualFunds.map(f => ({
    ...f,
    cleanedName: clean(f.name),
    cleanedTicker: clean(f.ticker),
    mode: rest.mode
  }));

  const { totalAmount } = useContext(CartContext);
  const [textValue, setTextValue] = useState(rest.defaultValue || '');

  useEffect(() => {
    setTextValue('');
  }, [totalAmount]);

  const getSuggestions = value => {
    // clean user input
    const cleanValue = clean(value.trim());

    if (cleanValue.length < AUTOSUGGEST_MIN_CHARS) return [];

    const regex = new RegExp(cleanValue, 'i');

    // US261841 - Do not display Portfolio 2021
    const results = cleanedFunds
      .filter(
        ({ cleanedName, cleanedTicker }) =>
          regex.test(cleanedName) || regex.test(cleanedTicker)
      )
      .filter(({ cleanedName }) => !cleanedName.includes('Portfolio2021'));

    return results.length
      ? sortBy(results, 'name')
      : ['Could Not Find Any Funds Available'];
  };

  const [suggestions, setSuggestions] = useState([]);

  const onSuggestionsClearRequested = () => setSuggestions([]);
  const onSuggestionsFetchRequested = ({ value }) =>
    setSuggestions(getSuggestions(value));

  const onSuggestionSelected = (event, { suggestionValue }) => {
    if (!updateFilterText) return;

    const getFilterTextParam = isCustodianFundValNone(
      rest.mode,
      suggestionValue
    );

    if (getFilterTextParam) {
      updateFilterText(textValue);
      return;
    }

    updateFilterText(suggestionValue);

    if (rest.mode === 'FONASU-portfolio') setTextValue('');
  };

  const onChange = (event, { newValue }) => {
    if (newValue === 'none') {
      return;
    }
    setTextValue(newValue);
    if (rest.mode === 'MI') {
      const { idx, fundQuickSearchFunds, cusips, setCusips } =
        rest?.cusipInfo || {};
      updateCusips(idx, newValue, fundQuickSearchFunds, cusips, setCusips);
    }
    if (!updateFilterText) return;
    if (clean(newValue.trim()).length === 0) updateFilterText('');
  };

  const onBlur = () => {
    if (!rest.onBlur) return;
    rest.onBlur(textValue);
  };

  const inputProps = {
    placeholder,
    value: textValue,
    onChange,
    onBlur
  };

  return (
    <div className="autosuggest-input">
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestions={getSuggestions}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
        renderInputComponent={renderInputComponent}
      />
      <style jsx>{styles}</style>
    </div>
  );
};

export default FundQuickSearch;
